import type { Theme, WithStyles } from '@material-ui/core'
import { createStyles, withStyles } from '@material-ui/core'
import type {
  OverridableValue,
  UnitAbbreviation,
  UpdateableArea,
  UpdateableAreaSubstrate
} from '@paintscout/util/builder'
import { getAreaSubstrateDimension, getObjectLabels, getArea, getAreaSubstrate } from '@paintscout/util/builder'
import type { OptionsDocument, QuoteDocument } from 'paintscout'
import React from 'react'
import classnames from 'classnames'
import CalculationElement from './CalculationElement'
import CalculationOperator from './CalculationOperator'
import CalculationTerm from './CalculationTerm'

export const styles = (_theme: Theme) => {
  return createStyles({
    root: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-around'
    },
    prepTerm: {
      display: 'flex'
    }
  })
}

export interface CalculationHelperProps extends WithStyles<typeof styles> {
  quote?: QuoteDocument
  options: OptionsDocument
  areaKey?: string
  areaSubstrateKey?: string
}

interface TermsComponents {
  rateType: string
  calculationType: string
  areaType: string
  hours: OverridableValue
  prep: number
  price: OverridableValue
  rateValue: OverridableValue
  activeDimension?: number
  unitLabel?: string
  hourlyRate: number
  currencySymbol: string
}

function CalculationHelper({ classes, quote, options, areaKey, areaSubstrateKey }: CalculationHelperProps) {
  const area = getArea({ quote, options, key: areaKey })
  const { currency, unit } = getObjectLabels({ options })
  const areaSubstrate = getAreaSubstrate({ quote, options, areaKey, key: areaSubstrateKey }) as UpdateableAreaSubstrate
  if (!area || !areaSubstrate) {
    return null
  }

  const hourlyRate =
    areaSubstrate && areaSubstrate.overrideHourlyRate && typeof areaSubstrate.hourlyRate === 'number'
      ? areaSubstrate.hourlyRate
      : quote.hourly_rate

  const { areaType } = area
  const { rate } = areaSubstrate
  const { rateType, calculationType } = rate
  const activeDimension = getActiveDimension(area, areaSubstrate)
  const unitLabel = getUnitLabel(rateType, unit.abbreviation)

  const { hours, prep, price, rateValue } = areaSubstrate
  const rateTerm = getRateTerm(
    {
      rateType,
      calculationType,
      areaType,
      hours,
      prep,
      price,
      rateValue,
      activeDimension,
      unitLabel,
      hourlyRate,
      currencySymbol: currency.symbol
    },
    classes
  )

  return <div className={classnames(classes.root, 'rate-formula')}>{rateTerm}</div>
}

export default withStyles(styles)(CalculationHelper)

function getUnitLabel(rateType: string, unit: UnitAbbreviation): string {
  switch (rateType) {
    case 'sqftWalls':
    case 'sqftCeiling':
    case 'sqftFloor':
      return unit.square
    case 'lnft':
      return unit.linear
    case 'cuft':
      return `cubic ${unit.square}`
    case 'quantity':
      return 'items'
    default:
      return ''
  }
}

export function getActiveDimension(area: UpdateableArea, areaSubstrate: UpdateableAreaSubstrate): number {
  const { dimensions, areaType } = area
  const { rate } = areaSubstrate
  const { rateType } = rate

  if (rateType === 'quantity') {
    return areaSubstrate.quantity
  }

  if (areaSubstrate.showOverrideDimensions) {
    return areaSubstrate.dimensionOverride?.useCustom
      ? areaSubstrate.dimensionOverride.custom
      : areaSubstrate.dimensionOverride?.default ?? 0
  }

  if (areaSubstrate.length.useCustom || areaSubstrate.height.useCustom || areaSubstrate.width.useCustom) {
    const length =
      areaSubstrate.length.useCustom && areaSubstrate.length?.custom != 0
        ? areaSubstrate.length.custom
        : areaSubstrate.length.default
    const height =
      areaSubstrate.height.useCustom && areaSubstrate.height.custom != 0
        ? areaSubstrate.height.custom
        : areaSubstrate.height.default
    const width =
      areaSubstrate.width.useCustom && areaSubstrate.width.custom != 0
        ? areaSubstrate.width.custom
        : areaSubstrate.width.default

    const dimensionValue = getAreaSubstrateDimension({
      length,
      width,
      height,
      isRoom: areaType === 'room',
      rateType: rateType,
      useInches: areaSubstrate?.useInches ?? false
    })

    return dimensionValue
  } else {
    const lnft = dimensions['lnft']?.useCustom ? dimensions['lnft'].custom : dimensions['lnft']?.default ?? 0
    const sqft = dimensions['sqft']?.useCustom ? dimensions['sqft'].custom : dimensions['sqft']?.default ?? 0
    const sqftWalls = dimensions['sqftWalls']?.useCustom
      ? dimensions['sqftWalls'].custom
      : dimensions['sqftWalls']?.default ?? 0
    const sqftFloor = dimensions['sqftFloor']?.useCustom
      ? dimensions['sqftFloor'].custom
      : dimensions['sqftFloor']?.default ?? 0
    const sqftCeiling = dimensions['sqftCeiling']?.useCustom
      ? dimensions['sqftCeiling'].custom
      : dimensions['sqftCeiling']?.default ?? 0
    const cuft = dimensions['cuft']?.useCustom ? dimensions['cuft'].custom : dimensions['cuft']?.default ?? 0

    if (rateType === 'lnft') {
      return lnft
    } else if (rateType === 'cuft') {
      return cuft
    } else if (rateType === 'sqftWalls') {
      return areaType === 'room' ? sqftWalls : sqft
    } else if (rateType === 'sqftFloor') {
      return areaType === 'room' ? sqftFloor : sqft
    } else if (rateType === 'sqftCeiling') {
      return areaType === 'room' ? sqftCeiling : sqft
    } else {
      return sqft
    }
  }
}

function getRateTerm(args: TermsComponents, classes?: any) {
  const { calculationType, rateType } = args
  switch (calculationType) {
    case 'amountPerHour':
      if (rateType === 'quantity') {
        return getHoursPerAmountRateTerm(args, classes)
      }
      return getAmountPerHourRateTerm(args, classes)
    case 'hoursPerAmount':
      return getHoursPerAmountRateTerm(args, classes)
    case 'pricePerAmount':
      return getPricePerAmountRateTerm(args, classes)
    case 'hours':
      return getHoursRateTerm(args, classes)
    case 'price':
      return getPriceRateTerm(args, classes)
  }
}
function getAmountPerHourRateTerm(args: TermsComponents, classes?: any) {
  const { areaType, activeDimension, unitLabel, rateValue, hours, prep, price, hourlyRate, currencySymbol } = args

  const rateLabel = `${unitLabel}/hr`

  const overrideHours = hours.useCustom

  const showPrep = prep > 0

  return (
    <CalculationTerm>
      <CalculationTerm parenthesis={true}>
        <CalculationTerm parenthesis={showPrep} classes={{ root: 'labor-term' }}>
          <CalculationTerm strike={overrideHours}>
            <CalculationElement
              label={areaType === 'surface' ? 'Surface' : 'Room'}
              value={activeDimension}
              sublabel={unitLabel.toLowerCase()}
              format={'dimension'}
            />
            <CalculationOperator label={'÷'} />
            <CalculationElement
              label={'Rate'}
              value={rateValue.useCustom ? rateValue.custom : rateValue.default}
              format={'rate'}
              sublabel={rateLabel.toLowerCase()}
            />
            <CalculationOperator label={'='} />
          </CalculationTerm>
          <CalculationElement
            label={'Labor'}
            sublabel={'hr'}
            value={hours.useCustom ? hours.custom : hours.default}
            format={'hours'}
          />
        </CalculationTerm>
        {showPrep && (
          <div className={classnames(classes.prepTerm, 'prep-term')}>
            <CalculationOperator label={'+'} classes={{ root: 'plus-prep' }} />
            <CalculationElement label={'Prep'} sublabel={'hr'} value={prep} format={'hours'} />
          </div>
        )}
      </CalculationTerm>
      <CalculationOperator label={'x'} />
      <CalculationElement label={'Hourly'} value={hourlyRate} format={'price'} sublabel={`${currencySymbol}/hr`} />
      <CalculationOperator label={'='} />
      <CalculationElement value={price.useCustom ? price.custom : price.default} color={'default'} format={'price'} />
    </CalculationTerm>
  )
}

function getHoursPerAmountRateTerm(args: TermsComponents, classes?: any) {
  const { areaType, activeDimension, unitLabel, rateValue, price, prep, hourlyRate, hours, currencySymbol } = args
  const rateLabel = `hrs/item`

  const prepTerm = prep ? (
    <div className={classnames(classes.prepTerm, 'prep-term')}>
      <CalculationOperator label={'+'} classes={{ root: 'plus-prep' }} />
      <CalculationElement label={'Prep'} sublabel={'hr'} value={prep} format={'hours'} />
    </div>
  ) : null

  return (
    <CalculationTerm>
      <CalculationTerm parenthesis={true} classes={{ root: 'labor-term' }}>
        <CalculationElement
          label={areaType === 'surface' ? 'Surface' : 'Room'}
          value={activeDimension}
          sublabel={unitLabel.toLowerCase()}
        />
        <CalculationOperator label={'x'} />
        <CalculationElement
          label={'Rate'}
          value={rateValue.useCustom ? rateValue.custom : rateValue.default}
          sublabel={rateLabel.toLowerCase()}
          format={'rate'}
        />
        <CalculationOperator label={'='} />
        <CalculationElement
          label={'Labor'}
          sublabel={'hr'}
          value={hours.useCustom ? hours.custom : hours.default}
          format={'hours'}
        />
        {prepTerm}
      </CalculationTerm>
      <CalculationOperator label={'x'} />
      <CalculationElement label={'Hourly'} value={hourlyRate} format={'price'} sublabel={`${currencySymbol}/hr`} />
      <CalculationOperator label={'='} />
      <CalculationElement value={price.useCustom ? price.custom : price.default} color={'default'} format={'price'} />
    </CalculationTerm>
  )
}

function getPricePerAmountRateTerm(args: TermsComponents, classes?: any) {
  const { areaType, activeDimension, unitLabel, rateValue, price, prep, hourlyRate, currencySymbol } = args

  const rateLabel = `${currencySymbol}/${unitLabel}`

  const prepTerm = prep ? (
    <div className={classnames(classes.prepTerm, 'prep-term')}>
      <CalculationOperator label={'+'} classes={{ root: 'plus-prep' }} />
      <CalculationTerm parenthesis={true}>
        <CalculationElement label={'Prep'} sublabel={'hr'} value={prep} format={'hours'} />
        <CalculationOperator label={'x'} />
        <CalculationElement label={'Hourly'} value={hourlyRate} format={'price'} sublabel={`${currencySymbol}/hr`} />
      </CalculationTerm>
    </div>
  ) : null

  return (
    <CalculationTerm>
      <CalculationTerm parenthesis={true} classes={{ root: 'labor-term' }}>
        <CalculationElement
          label={areaType === 'surface' ? 'Surface' : 'Room'}
          value={activeDimension}
          sublabel={unitLabel.toLowerCase()}
        />
        <CalculationOperator label={'x'} />
        <CalculationElement
          label={'Rate'}
          value={rateValue.useCustom ? rateValue.custom : rateValue.default}
          sublabel={rateLabel.toLowerCase()}
          format={'rate'}
        />
      </CalculationTerm>
      {prepTerm}
      <CalculationOperator label={'='} />
      <CalculationElement value={price.useCustom ? price.custom : price.default} color={'default'} format={'price'} />
    </CalculationTerm>
  )
}

function getHoursRateTerm(args: TermsComponents, _classes?: any) {
  const { hours, prep, hourlyRate, price, currencySymbol } = args

  return (
    <CalculationTerm>
      <CalculationTerm parenthesis={true}>
        <CalculationElement
          label={'Labor'}
          sublabel={'hr'}
          value={hours.useCustom ? hours.custom : hours.default}
          format={'hours'}
        />
        <CalculationOperator label={'+'} />
        <CalculationElement label={'Prep'} sublabel={'hr'} format={'hours'} value={prep} />
      </CalculationTerm>
      <CalculationOperator label={'x'} />
      <CalculationElement label={'Hourly'} value={hourlyRate} format={'price'} sublabel={`${currencySymbol}/hr`} />
      <CalculationOperator label={'='} />
      <CalculationElement value={price.useCustom ? price.custom : price.default} color={'default'} format={'price'} />
    </CalculationTerm>
  )
}

function getPriceRateTerm(args: TermsComponents, _classes?: any) {
  const { hours, hourlyRate, price, currencySymbol } = args

  return (
    <CalculationTerm>
      <CalculationTerm strike={true}>
        <CalculationElement
          label={'Labor'}
          sublabel={'hr'}
          value={hours.useCustom ? hours.custom : hours.default}
          format={'hours'}
        />
        <CalculationOperator label={'x'} />
        <CalculationElement label={'Hourly'} value={hourlyRate} format={'price'} sublabel={`${currencySymbol}/hr`} />
        <CalculationOperator label={'='} />
      </CalculationTerm>
      <CalculationElement value={price.useCustom ? price.custom : price.default} color={'default'} format={'price'} />
    </CalculationTerm>
  )
}
